import { HostedFields, Client, client } from 'braintree-web';
import { HostedFieldsEvent, HostedFieldsHostedFieldsCard } from 'braintree-web/hosted-fields';
import classNames from 'classnames';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { Lock } from '@zooplus/icons';
import { Alert, Checkbox } from '@zooplus/ui-components';

import CardIcon from '@components/CardIcon/CardIcon';
import CVVIcon from '@components/CVVIcon/CVVIcon';
import FormControl from '@components/FormControl/FormControl';
import FormControlWrapper from '@components/FormControlWrapper/FormControlWrapper';
import useIsMobile from '@hooks/useIsMobile';
import { useTracking } from '@hooks/useTracking';
import { useTranslation } from '@hooks/useTranslations';
import { useAppContext } from '@providers/AppContextProvider';
import { useConfig } from '@providers/ConfigProvider';
import { useErrorContext } from '@providers/ErrorContextProvider';
import { sanitizeCardType, sanitizeCardTypes } from '@services/braintreeService';
import { braintreeTracking } from '@services/diiService';
import { captureException } from '@services/sentryService';
import { CardType, ContainerType, Sizes } from '@types-internal';
import { TokenizeCardResult } from '@types-internal/BraintreeTypes';
import {
  braintreeTokenizeCard,
  getBraintreeStatusCallbacks,
  useBraintreeHostedFields,
} from '@utils/braintreeUtils';

import styles from './BraintreeCreditCardForm.module.scss';

type FieldName = 'number' | 'cardholderName' | 'expirationMonth' | 'expirationYear' | 'cvv';

const DISPLAYABLE_CARD_TYPES: CardType[] = [
  'visa',
  'mastercard',
  'american-express',
  'diners-club',
  'maestro',
];

// Override since typescript types for braintree are not inline with actual implementation ...
export type HostedFieldsOverride = HostedFields & {
  getSupportedCardTypes: () => Promise<string[]>;
};

export type BraintreeCreditCardFormHandle = {
  tokenizeCard: () => Promise<TokenizeCardResult>;
};

export type BraintreeCreditCardFormProps = {
  token: string;
  containerType: ContainerType;
  error?: string;
  labelsInline: boolean;
  allowVaulting: boolean;
  onFatalError?: (error: any) => void;
  currency?: string;
  customerId?: string;
  customerType?: string;
  sid?: string;
  siteId?: number;
};

type FlagByFieldName = { [key in FieldName]?: boolean };

const BraintreeCreditCardForm = forwardRef<
  BraintreeCreditCardFormHandle,
  BraintreeCreditCardFormProps
>(
  (
    {
      token,
      containerType,
      error,
      labelsInline,
      allowVaulting,
      onFatalError,
      currency,
      customerId,
      customerType,
      sid,
      siteId,
    },
    forwardedRef,
  ) => {
    const braintreeInstance = useRef<Client>();
    const hostedFieldsInstance = useRef<HostedFieldsOverride>();
    const { t } = useTranslation();
    const appContext = useAppContext();

    const track = useTracking({
      currency,
      customerId,
      sid,
      siteId,
      guest: customerType === 'GUEST_CUSTOMER',
    });
    const { setError } = useErrorContext();
    const {
      config: { env },
    } = useConfig();
    const isLocalEnv = env === 'local';
    const isMobile = useIsMobile(Sizes.MD);

    const [storeInVault, setStoreInVault] = useState(false);
    const [supportedCardTypes, setSupportedCardTypes] = useState<CardType[]>([]);
    const [hostedFieldsCards, setHostedFieldsCards] = useState<HostedFieldsHostedFieldsCard[]>([]);
    const [intialized, setInitialized] = useState(false);

    const [fieldsValidity, setFieldsValidity] = useState<FlagByFieldName>({});
    const [fieldsEmpty, setFieldsEmpty] = useState<FlagByFieldName>({});
    const makeHostedFields = useBraintreeHostedFields(braintreeInstance, false);

    const commonCards = hostedFieldsCards.filter((element) =>
      supportedCardTypes.includes(sanitizeCardType(element.type)),
    );
    const selectedCardType = commonCards.length == 1 ? sanitizeCardType(commonCards[0].type) : null;

    const updateValidity = (newFlagByFieldName: FlagByFieldName) => {
      setFieldsValidity((oldFlagByFieldName) => ({ ...oldFlagByFieldName, ...newFlagByFieldName }));
    };

    const updateEmpty = (newFlagByFieldName: FlagByFieldName) => {
      setFieldsEmpty((oldFlagByFieldName) => ({ ...oldFlagByFieldName, ...newFlagByFieldName }));
    };

    const { isInvalid, isInvalidEmpty, isCardTypeActive } = getBraintreeStatusCallbacks(
      intialized,
      fieldsValidity,
      hostedFieldsCards,
      supportedCardTypes,
      fieldsEmpty,
    );

    // Since braintree validity does not take supported card types into account
    // (a card can be of an ussuported type but considered valid anyway)
    // We need a manual validation of card type
    const isCurrentCardNumberSupported = useCallback(() => {
      return (
        !intialized ||
        !!hostedFieldsCards.find((card) => supportedCardTypes.includes(sanitizeCardType(card.type)))
      );
    }, [intialized, hostedFieldsCards, supportedCardTypes]);

    const registerHostedFieldsCallbacks = useCallback(() => {
      hostedFieldsInstance.current?.on('validityChange', (event: HostedFieldsEvent) => {
        if (isLocalEnv) console.log('Card validity changed :', event.fields);
        updateValidity({ [event.emittedBy as FieldName]: event.fields[event.emittedBy]?.isValid });
      });
      hostedFieldsInstance.current?.on('empty', (event: HostedFieldsEvent) => {
        if (isLocalEnv) console.log('Card field empty changed :', event.fields);
        updateEmpty({ [event.emittedBy as FieldName]: event.fields[event.emittedBy]?.isEmpty });
      });
      hostedFieldsInstance.current?.on('notEmpty', (event: HostedFieldsEvent) => {
        if (isLocalEnv) console.log('Card field empty changed :', event.fields);
        updateEmpty({ [event.emittedBy as FieldName]: event.fields[event.emittedBy]?.isEmpty });
      });
      hostedFieldsInstance.current?.on('cardTypeChange', (event: HostedFieldsEvent) => {
        if (isLocalEnv) console.log('Card type changed :', event.cards);
        setHostedFieldsCards(event.cards);
      });
    }, []);

    const handleFatalError = useCallback(
      (error: any) => {
        onFatalError?.(error);
        setError({
          message: t('checkout.payment.creditcard.error.method.unavailable'),
          retryable: false,
        });
      },
      [onFatalError, t, setError],
    );

    const createHostedFields = useCallback(async () => {
      if (isLocalEnv) console.log('Creating new hosted fields instance...');
      try {
        hostedFieldsInstance.current = await makeHostedFields();
        if (isLocalEnv) console.log('Braintree hosted fields instance created.');
        setHostedFieldsCards(hostedFieldsInstance.current.getState().cards);
        const types = await hostedFieldsInstance.current.getSupportedCardTypes();
        if (isLocalEnv) console.log('Supported card nice types', types);
        const cardTypes = sanitizeCardTypes(types);
        if (appContext?.shopBundle === 'bitiba' && cardTypes.includes('american-express')) {
          cardTypes.splice(cardTypes.indexOf('american-express'), 1);
        }
        setSupportedCardTypes(cardTypes);
        registerHostedFieldsCallbacks();
        setInitialized(true);
      } catch (error: any) {
        switch (error.code) {
          case 'HOSTED_FIELDS_FIELD_DUPLICATE_IFRAME':
            track?.('BTformKO', error.message);
            if (!track) {
              braintreeTracking('BTformKO', error.message);
            }
            break;
          default:
            captureException({ error, message: 'Braintree hosted fields cannot be created' });
            handleFatalError(error);
            break;
        }
      }
    }, [t, registerHostedFieldsCallbacks, handleFatalError]);

    useEffect(() => {
      (async () => {
        try {
          if (isLocalEnv) console.log('Creating new braintree instance...');
          braintreeInstance.current = await client.create({ authorization: token });
        } catch (error: any) {
          // error.code = 'INSTANTIATION_OPTION_REQUIRED' when token is not set
          // error.code = 'CLIENT_AUTHORIZATION_INVALID' when token is not valid
          handleFatalError(error);
          captureException({ error, message: 'Cannot create Braintree client' });
          throw error;
        }
        createHostedFields();
      })();
    }, [createHostedFields, handleFatalError, token]);

    const tokenizeCard = useCallback(
      braintreeTokenizeCard({
        setError,
        hostedFieldsInstance,
        isCurrentCardNumberSupported,
        updateValidity,
        updateEmpty,
        isLocalEnv,
        storeInVault,
        containerType,
        track,
      }),
      [isCurrentCardNumberSupported, setError, storeInVault, containerType],
    );

    useEffect(() => {
      if (Object.values(fieldsValidity).every((v) => v)) {
        setError(undefined);
      }
    }, [fieldsValidity, setError]);

    // For calling from within Ract application
    useImperativeHandle(forwardedRef, () => ({
      tokenizeCard,
    }));

    const handleStoreInVaultChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
      setStoreInVault(event.target.checked);
    };

    const isAmex = hostedFieldsCards.length === 1 && isCardTypeActive('american-express');
    const isSupported = isCurrentCardNumberSupported();

    return (
      <div data-zta="braintree-cc-form-container">
        {error && (
          <Alert className={styles.errorMessage} variant="warning">
            {error}
          </Alert>
        )}
        <div className={styles.activeCards}>
          {selectedCardType
            ? isMobile && (
                <CardIcon
                  type={selectedCardType}
                  key={selectedCardType}
                  className={styles.selectedCard}
                />
              )
            : DISPLAYABLE_CARD_TYPES.filter((type) => supportedCardTypes.includes(type)).map(
                (type) => {
                  return (
                    <CardIcon
                      type={type}
                      key={type}
                      className={classNames(isCardTypeActive(type) && styles.activeCard)}
                    />
                  );
                },
              )}
        </div>
        <div data-zta="braintree-cc-form">
          <FormControlWrapper
            label={t('checkout.payment.creditcard.card_number')}
            labelFor="cc-number"
            labelInline={labelsInline}
            infoMessage={t('checkout.payment.secure')}
            infoMessageIcon={<Lock />}
            errorMessage={
              isInvalidEmpty('number')
                ? t('checkout.form.required.error')
                : !isSupported
                  ? t('checkout.payment.creditcard.type_not_supported')
                  : isInvalid('number') &&
                    t('checkout.payment.creditcard.error.form.cardnumber.invalid')
            }
            errorZta="errorInputCreditCardNumber">
            <FormControl
              innerClassname={styles.noPadding}
              id="cc-number"
              appendixItems={
                selectedCardType &&
                !isMobile && (
                  <CardIcon
                    type={selectedCardType}
                    key={selectedCardType}
                    className={styles.selectedCard}
                  />
                )
              }
              hasError={!isSupported || isInvalid('number') || isInvalidEmpty('number')}
              data-zta="inputCreditCardNumber"
            />
          </FormControlWrapper>
          <FormControlWrapper
            label={t('checkout.payment.creditcard.holder_name')}
            labelFor="cardholder-name"
            labelInline={labelsInline}
            errorMessage={isInvalidEmpty('cardholderName') && t('checkout.form.required.error')}
            errorZta="errorInputCardName">
            <FormControl
              innerClassname={styles.noPadding}
              hasError={isInvalid('cardholderName') || isInvalidEmpty('cardholderName')}
              id="cardholder-name"
              data-zta="inputCardName"
            />
          </FormControlWrapper>
          <FormControlWrapper
            label={t('checkout.payment.creditcard.expiry_date')}
            labelFor="expiration-month"
            labelInline={labelsInline}
            errorMessage={
              (isInvalidEmpty('expirationMonth') || isInvalidEmpty('expirationYear')) &&
              t('checkout.form.required.error')
            }
            errorZta="errorInputCardDate">
            <div className={styles.expirationDateContainer}>
              <FormControl
                select
                className={styles.expirationDateMonth}
                hasError={isInvalid('expirationMonth') || isInvalidEmpty('expirationMonth')}
                id="expiration-month"
                data-zta="inputCardMonth"
              />
              <FormControl
                select
                className={styles.expirationDateYear}
                hasError={isInvalid('expirationYear') || isInvalidEmpty('expirationYear')}
                id="expiration-year"
                data-zta="inputCardYear"
              />
            </div>
          </FormControlWrapper>
          <FormControlWrapper
            label={
              isAmex
                ? t('checkout.payment.creditcard.cid.generic_code')
                : t('checkout.payment.creditcard.cvc.generic_code')
            }
            labelFor="cc-cvv"
            labelInline={labelsInline}
            infoMessage={
              isAmex
                ? t('checkout.payment.creditcard.cid.warning')
                : t('checkout.payment.creditcard.cvc.warning')
            }
            errorMessage={
              isInvalid('cvv')
                ? t('checkout.form.cvc.min.length.error', { '0': isAmex ? 4 : 3 })
                : isInvalidEmpty('cvv') && t('checkout.form.required.error')
            }
            errorZta="errorInputCardCode">
            <FormControl
              className={styles.cvv}
              innerClassname={styles.noPadding}
              id="cc-cvv"
              appendixItems={<CVVIcon isAmex={isAmex} />}
              hasError={isInvalid('cvv') || isInvalidEmpty('cvv')}
              data-zta="inputCardCode"></FormControl>
          </FormControlWrapper>
          {allowVaulting && (
            <FormControlWrapper labelInline={labelsInline}>
              <Checkbox
                className={styles.vaultCheck}
                id="vault-check"
                checked={storeInVault}
                onChange={handleStoreInVaultChange}
                data-zta="storeCreditCard">
                {t('checkout.payment.save.creditcard')}
              </Checkbox>
            </FormControlWrapper>
          )}
        </div>
      </div>
    );
  },
);

export default BraintreeCreditCardForm;
